import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  CloseButton,
  Button,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
  Spinner,
  Collapse,
} from "react-bootstrap";
import ActivateSimModal from "./modals/ActivateSimModal";
import { ExternalLink } from "react-feather";
import SimsMap from "./SimsMap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SuspendSimModal from "./modals/SuspendSimModal";
import ResumeSimModal from "./modals/ResumeSimModal";
import TerminateSimModal from "./modals/TerminateSimModal";
import { useDispatch, useSelector } from "react-redux";
import useWindowSize from "../../../hooks/useWindowSize";
import { getSimCellInfo } from "../../../redux/slices/sims";
import Loader from "../../../components/spinners/Loader";
import useDeviceType from "../../../hooks/useDeviceType";

function Single(props) {
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openActivateModal, setOpenActivateModal] = useState(false);
  const [openSuspendModal, setOpenSuspendModal] = useState(false);
  const [openResumeModal, setOpenResumeModal] = useState(false);
  const [openTerminateModal, setOpenTerminateModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const openResumeModalRef = useRef(null);
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(props.open);
  const [fadeOut, setFadeOut] = useState(false);
  const isDemoOrLead = props.demoMode || activeUser.role === "lead";
  const [isLoading, setIsLoading] = useState(isDemoOrLead ? false : true);
  const dispatch = useDispatch();
  const storeCellInfo = useSelector((state) => state.sims.cellInfo);
  const dummyCellInfo = {
    latitude: 40.6401, // Approx. Thessaloniki
    longitude: 22.9444,
    cell: 12345, // Dummy cell ID
    area: 678, // Dummy area code (LAC/TAC)
    range: 1000, // In meters
    unit: "", // Distance unit
  };
  const cellInfo = !isDemoOrLead ? storeCellInfo : dummyCellInfo;

  useEffect(() => {
    setIsLoading(!isDemoOrLead);
  }, [isDemoOrLead]);

  useEffect(() => {
    const fetchSimCellInfo = async () => {
      if (
        props.shownSim.state !== "OnStock" &&
        props.shownSim.state !== "Deleted" &&
        activeUser.role !== "lead"
      ) {
        setIsLoading(true);
        await dispatch(getSimCellInfo(props.shownSim.imsi)).finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        });
      } else {
        setIsLoading(false);
      }
    };

    if (props.shownSim && props.shownSim.id && activeUser.role !== "lead") {
      fetchSimCellInfo();
    }
  }, [props.shownSim, dispatch]);

  useEffect(() => {
    if (props.open && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [props.open, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      props.setOpen(false);
    }, 300);
  };

  const renderTooltip = (tooltipProps) => (
    <Tooltip id="button-tooltip" {...tooltipProps}>
      {props.demoMode
        ? "This is not available in demo mode."
        : "You lack necessary permissions, please contact your parent account owner."}
    </Tooltip>
  );

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  if (isDesktop) {
    if (props.shownSim && props.shownSim.id) {
      return (
        <>
          <Card>
            <Card.Header>
              <Row>
                <Col xxl="6">
                  <Card.Title className="mb-0">
                    SIM {t("Information")}
                  </Card.Title>
                </Col>
                <Col xxl="6">
                  <CloseButton
                    className="float-end"
                    onClick={() => {
                      props.setSingleIsShown(false);
                      setTimeout(function () {
                        props.setOpen(false);
                      }, 200);
                    }}
                  />
                </Col>
              </Row>
            </Card.Header>
            {isLoading && <Loader />}

            <Collapse in={!isLoading} dimension="height">
              <div>
                <Card.Body>
                  {activeUser.role !== "admin" && (
                    <Row className="g-0">
                      <strong>Actions</strong>
                      <div
                        className="d-flex flex-wrap mt-2"
                        id="simsActionsPanel"
                      >
                        {(props.shownSim.state === "OnStock" ||
                          props.shownSim.state === "TestProductive") && (
                          <>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                (tooltipProps) =>
                                  props.demoMode ||
                                  (activeUser.role === "subaccount" &&
                                    !activeUser.privileges.some(
                                      (privilege) =>
                                        privilege.name === "ActivateSIM"
                                    )) ? (
                                    renderTooltip(tooltipProps)
                                  ) : (
                                    <></>
                                  ) // Use null instead of <></>
                              }
                            >
                              <span className="d-inline-block">
                                <Button
                                  variant="success"
                                  className="me-2 mb-1 px-2"
                                  id="simsActionActivate"
                                  style={{
                                    "--dynamic-bg-color":
                                      whitelabel?.backgroundColor ?? "#0E1A2B",
                                    "--dynamic-font-color":
                                      whitelabel?.fontColor ?? "#fff",
                                    border: "none",
                                  }}
                                  onClick={async () => {
                                    setOpenActivateModal(true);
                                  }}
                                  disabled={
                                    props.demoMode ||
                                    (activeUser.role === "subaccount" &&
                                      !activeUser.privileges.some(
                                        (privilege) =>
                                          privilege.name === "ActivateSIM"
                                      )) ||
                                    activeUser.role === "lead"
                                  }
                                >
                                  <span> {t("Activate")}</span>
                                </Button>
                              </span>
                            </OverlayTrigger>
                          </>
                        )}
                        {props.shownSim.state === "Productive" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={(tooltipProps) =>
                              props.demoMode ||
                              (activeUser.role === "subaccount" &&
                                !activeUser.privileges.some(
                                  (privilege) => privilege.name === "SuspendSIM"
                                )) ? (
                                renderTooltip(tooltipProps)
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span className="d-inline-block">
                              <Button
                                variant="warning"
                                className="me-2 mb-1 px-2"
                                id="simsActionSuspend"
                                onClick={async () => {
                                  setOpenSuspendModal(true);
                                }}
                                style={{
                                  "--dynamic-bg-color":
                                    whitelabel?.backgroundColor ?? "#0E1A2B",
                                  "--dynamic-font-color":
                                    whitelabel?.fontColor ?? "#fff",
                                  border: "none",
                                }}
                                disabled={
                                  props.demoMode ||
                                  (activeUser.role === "subaccount" &&
                                    !activeUser.privileges.some(
                                      (privilege) =>
                                        privilege.name === "SuspendSIM"
                                    )) ||
                                  activeUser.role === "lead"
                                }
                              >
                                <span> {t("Suspend")}</span>
                              </Button>
                            </span>
                          </OverlayTrigger>
                        )}
                        {(props.shownSim.state === "Suspended" ||
                          props.shownSim.state === "AutoSuspended") && (
                          <OverlayTrigger
                            placement="top"
                            overlay={(tooltipProps) =>
                              props.demoMode ||
                              (activeUser.role === "subaccount" &&
                                !activeUser.privileges.some(
                                  (privilege) => privilege.name === "ResumeSIM"
                                )) ? (
                                renderTooltip(tooltipProps)
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span className="d-inline-block">
                              <Button
                                variant="primary"
                                className="me-2 mb-1 px-2"
                                id="simsActionResume"
                                onClick={async () => {
                                  setOpenResumeModal(true);
                                }}
                                style={{
                                  "--dynamic-bg-color":
                                    whitelabel?.backgroundColor ?? "#0E1A2B",
                                  "--dynamic-font-color":
                                    whitelabel?.fontColor ?? "#fff",
                                  border: "none",
                                }}
                                disabled={
                                  props.demoMode ||
                                  (activeUser.role === "subaccount" &&
                                    !activeUser.privileges.some(
                                      (privilege) =>
                                        privilege.name === "ResumeSIM"
                                    )) ||
                                  activeUser.role === "lead"
                                }
                              >
                                <span> {t("Resume")}</span>
                              </Button>
                            </span>
                          </OverlayTrigger>
                        )}
                        {(props.shownSim.state === "Productive" ||
                          props.shownSim.state === "Suspended" ||
                          props.shownSim.state === "AutoSuspended") && (
                          <OverlayTrigger
                            placement="top"
                            overlay={(tooltipProps) =>
                              props.demoMode ||
                              (activeUser.role === "subaccount" &&
                                !activeUser.privileges.some(
                                  (privilege) =>
                                    privilege.name === "TerminateSIM"
                                )) ? (
                                renderTooltip(tooltipProps)
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span className="d-inline-block">
                              <Button
                                variant="danger"
                                className="me-2 mb-1 px-2"
                                id="simsActionTerminate"
                                onClick={async () => {
                                  setOpenTerminateModal(true);
                                  await new Promise((resolve) => {
                                    const interval = setInterval(() => {
                                      if (!openResumeModalRef.current) {
                                        clearInterval(interval);
                                        resolve();
                                      }
                                    }, 500);
                                  });
                                }}
                                style={{
                                  "--dynamic-bg-color":
                                    whitelabel?.backgroundColor ?? "#0E1A2B",
                                  "--dynamic-font-color":
                                    whitelabel?.fontColor ?? "#fff",
                                  border: "none",
                                }}
                                disabled={
                                  props.demoMode ||
                                  (activeUser.role === "subaccount" &&
                                    !activeUser.privileges.some(
                                      (privilege) =>
                                        privilege.name === "TerminateSIM"
                                    )) ||
                                  activeUser.role === "lead"
                                }
                              >
                                <span> {t("Terminate")}</span>
                              </Button>
                            </span>
                          </OverlayTrigger>
                        )}
                      </div>
                    </Row>
                  )}
                  <Table size="sm" className="my-2">
                    <tbody>
                      <tr>
                        <th>{t("Usage")}</th>
                        <td>
                          {props.shownSim.totalBytes !== null
                            ? `${parseFloat(props.shownSim.totalMBytes).toFixed(
                                3
                              )}MB`
                            : "0.00MB"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t("IP Address")}</th>
                        <td>
                          {props.shownSim.ipAddress !== null
                            ? props.shownSim.ipAddress
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <th>IMEI</th>
                        <td>
                          {props.shownSim.imei !== null
                            ? props.shownSim.imei
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t("Status")}</th>
                        <td>
                          <Badge
                            bg={
                              props.shownSim.status === "Productive"
                                ? "primary"
                                : props.shownSim.status === "Active"
                                ? "success"
                                : props.shownSim.status === "Suspended"
                                ? "warning"
                                : props.shownSim.status === "Terminated"
                                ? "danger"
                                : props.shownSim.status === "OnStock"
                                ? "light"
                                : props.shownSim.status === "Deleted"
                                ? "danger"
                                : props.shownSim.status === "TestProductive"
                                ? "primary"
                                : props.shownSim.status === "AutoSuspended"
                                ? "warning"
                                : null
                            }
                          >
                            {t(props.shownSim.status)}
                          </Badge>
                        </td>
                      </tr>
                      {props.shownSim.customFieldValues &&
                        props.shownSim.customFieldValues.length > 0 && (
                          <React.Fragment>
                            {props.shownSim.customFieldValues.map(
                              (customFieldValue) => {
                                return (
                                  <tr>
                                    <th>{customFieldValue.displayName}</th>
                                    <td>{customFieldValue.value}</td>
                                  </tr>
                                );
                              }
                            )}
                          </React.Fragment>
                        )}
                      {!isDemoOrLead && (
                        <tr>
                          <th>{t("Visit Profile")}</th>
                          <td>
                            <span
                              onClick={() => {
                                if (!isDemoOrLead) {
                                  navigate(
                                    `/sims/profile/${props.shownSim.imsi}`
                                  );
                                }
                              }}
                            >
                              <ExternalLink size={18} disabled={isDemoOrLead} />
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  <hr />
                  <strong>{t("Map")}</strong>
                  <SimsMap
                    lat={parseFloat(cellInfo?.latitude)}
                    lon={parseFloat(cellInfo?.longitude)}
                    className="my-3"
                  />
                  <Table size="sm">
                    <tbody>
                      <tr>
                        <th>{t("Provider")}</th>
                        <td>{props.shownSim.sku}</td>
                      </tr>
                      <tr>
                        <th>ICCID</th>
                        <td>{props.shownSim.iccid}</td>
                      </tr>
                      <tr>
                        <th>MSISDN</th>
                        <td>
                          {props.shownSim.msisdn !== ""
                            ? props.shownSim.msisdn
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <th>IMSI</th>
                        <td>
                          {props.shownSim.imsi !== ""
                            ? props.shownSim.imsi
                            : "N/A"}
                        </td>
                      </tr>
                      {props.shownSim.offer && (
                        <tr>
                          <th>Offer</th>
                          <td>
                            {props.shownSim.offer.name !== ""
                              ? props.shownSim.offer.name
                              : "N/A"}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <th>{t("Area")}</th>
                        <td>{cellInfo?.area ? cellInfo?.area : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("Cell")}</th>
                        <td>{cellInfo?.cell ? cellInfo?.cell : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("Longitude")}</th>
                        <td>
                          {cellInfo?.longitude ? cellInfo?.longitude : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t("Latitude")}</th>
                        <td>
                          {cellInfo?.latitude ? cellInfo?.latitude : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t("Cell Range")}</th>
                        <td>{cellInfo?.range ? cellInfo?.range : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("Unit")}</th>
                        <td>{cellInfo?.unit ? cellInfo?.unit : "N/A"}</td>
                      </tr>
                    </tbody>
                  </Table>
                  <hr />

                  <strong>{t("Activity")}</strong>

                  <ul className="timeline mt-2">
                    {props.shownSim.simActivity.map(
                      (activityElement, index) => (
                        <React.Fragment key={index}>
                          {activityElement.type === "ACTIVATE" && (
                            <li className="timeline-item">
                              <strong>Activated</strong>
                              <span className="float-end text-muted text-sm">
                                {activityElement.createdAt}
                              </span>
                              <p>{activityElement.description}</p>
                            </li>
                          )}
                          {activityElement.type === "SUSPEND" && (
                            <li className="timeline-item">
                              <strong>Suspended</strong>
                              <span className="float-end text-muted text-sm">
                                {activityElement.createdAt}
                              </span>
                              <p>{activityElement.description}</p>
                            </li>
                          )}
                          {activityElement.type === "RESUME" && (
                            <li className="timeline-item">
                              <strong>Resumed</strong>
                              <span className="float-end text-muted text-sm">
                                {activityElement.createdAt}
                              </span>
                              <p>{activityElement.description}</p>
                            </li>
                          )}
                          {activityElement.type === "MODIFY" && (
                            <li className="timeline-item">
                              <strong>Modified</strong>
                              <span className="float-end text-muted text-sm">
                                {activityElement.createdAt}
                              </span>
                              <p>{activityElement.description}</p>
                            </li>
                          )}
                          {activityElement.type === "TERMINATE" && (
                            <li className="timeline-item">
                              <strong>Terminated</strong>
                              <span className="float-end text-muted text-sm">
                                {activityElement.createdAt}
                              </span>
                              <p>{activityElement.description}</p>
                            </li>
                          )}
                        </React.Fragment>
                      )
                    )}
                  </ul>
                  <ActivateSimModal
                    openActivateModal={openActivateModal}
                    selectedSims={[props.shownSim]}
                    setOpenActivateModal={setOpenActivateModal}
                    clearSelectedRows={props.clearSelectedRows}
                  />
                  <SuspendSimModal
                    openSuspendModal={openSuspendModal}
                    sims={[props.shownSim]}
                    setOpenSuspendModal={setOpenSuspendModal}
                    clearSelectedRows={props.clearSelectedRows}
                  />
                  <ResumeSimModal
                    openResumeModal={openResumeModal}
                    sims={[props.shownSim]}
                    setOpenResumeModal={setOpenResumeModal}
                    clearSelectedRows={props.clearSelectedRows}
                  />
                  <TerminateSimModal
                    openTerminateModal={openTerminateModal}
                    sims={[props.shownSim]}
                    setOpenTerminateModal={setOpenTerminateModal}
                    clearSelectedRows={props.clearSelectedRows}
                  />
                </Card.Body>
              </div>
            </Collapse>
          </Card>
        </>
      );
    } else {
      return <Card></Card>;
    }
  } else {
    return (
      <React.Fragment>
        <Offcanvas
          show={internalShow}
          placement="bottom"
          backdrop="static"
          scrollable={true}
          className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
          onHide={handleHide}
        >
          <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
            <Offcanvas.Title>SIM {t("Information")}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="fullscreen-offcanvas-body">
            {props.shownSim && props.shownSim.id && (
              <React.Fragment>
                {activeUser.role !== "admin" && (
                  <Row className="g-0">
                    <strong>Actions</strong>
                    <div
                      className="d-flex flex-wrap mt-2"
                      id="simsActionsPanel"
                    >
                      {(props.shownSim.state === "OnStock" ||
                        props.shownSim.state === "TestProductive") && (
                        <>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              activeUser.role === "subaccount" &&
                              !activeUser.privileges.some(
                                (privilege) => privilege.name === "ActivateSIM"
                              ) ? (
                                renderTooltip
                              ) : (
                                <></>
                              ) // Use null instead of <></>
                            }
                          >
                            <span className="d-inline-block">
                              <Button
                                variant="success"
                                className="me-2 mb-1 px-2"
                                id="simsActionActivate"
                                style={{
                                  "--dynamic-bg-color":
                                    whitelabel?.backgroundColor ?? "#0E1A2B",
                                  "--dynamic-font-color":
                                    whitelabel?.fontColor ?? "#fff",
                                  border: "none",
                                }}
                                onClick={async () => {
                                  handleHide();
                                  setTimeout(() => {
                                    setOpenActivateModal(true);
                                  }, 350);
                                }}
                                disabled={
                                  (activeUser.role === "subaccount" &&
                                    !activeUser.privileges.some(
                                      (privilege) =>
                                        privilege.name === "ActivateSIM"
                                    )) ||
                                  activeUser.role === "lead"
                                }
                              >
                                <span> {t("Activate")}</span>
                              </Button>
                            </span>
                          </OverlayTrigger>
                        </>
                      )}
                      {props.shownSim.state === "Productive" && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            activeUser.role === "subaccount" &&
                            !activeUser.privileges.some(
                              (privilege) => privilege.name === "SuspendSIM"
                            ) ? (
                              renderTooltip
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className="d-inline-block">
                            <Button
                              variant="warning"
                              className="me-2 mb-1 px-2"
                              id="simsActionSuspend"
                              onClick={async () => {
                                handleHide();
                                setTimeout(() => {
                                  setOpenSuspendModal(true);
                                }, 350);
                              }}
                              style={{
                                "--dynamic-bg-color":
                                  whitelabel?.backgroundColor ?? "#0E1A2B",
                                "--dynamic-font-color":
                                  whitelabel?.fontColor ?? "#fff",
                                border: "none",
                              }}
                              disabled={
                                (activeUser.role === "subaccount" &&
                                  !activeUser.privileges.some(
                                    (privilege) =>
                                      privilege.name === "SuspendSIM"
                                  )) ||
                                activeUser.role === "lead"
                              }
                            >
                              <span> {t("Suspend")}</span>
                            </Button>
                          </span>
                        </OverlayTrigger>
                      )}
                      {(props.shownSim.state === "Suspended" ||
                        props.shownSim.state === "AutoSuspended") && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            activeUser.role === "subaccount" &&
                            !activeUser.privileges.some(
                              (privilege) => privilege.name === "ResumeSIM"
                            ) ? (
                              renderTooltip
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className="d-inline-block">
                            <Button
                              variant="primary"
                              className="me-2 mb-1 px-2"
                              id="simsActionResume"
                              onClick={async () => {
                                handleHide();
                                setTimeout(() => {
                                  setOpenResumeModal(true);
                                }, 350);
                              }}
                              style={{
                                "--dynamic-bg-color":
                                  whitelabel?.backgroundColor ?? "#0E1A2B",
                                "--dynamic-font-color":
                                  whitelabel?.fontColor ?? "#fff",
                                border: "none",
                              }}
                              disabled={
                                (activeUser.role === "subaccount" &&
                                  !activeUser.privileges.some(
                                    (privilege) =>
                                      privilege.name === "ResumeSIM"
                                  )) ||
                                activeUser.role === "lead"
                              }
                            >
                              <span> {t("Resume")}</span>
                            </Button>
                          </span>
                        </OverlayTrigger>
                      )}
                      {(props.shownSim.state === "Productive" ||
                        props.shownSim.state === "Suspended" ||
                        props.shownSim.state === "AutoSuspended") && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            activeUser.role === "subaccount" &&
                            !activeUser.privileges.some(
                              (privilege) => privilege.name === "TerminateSIM"
                            ) ? (
                              renderTooltip
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className="d-inline-block">
                            <Button
                              variant="danger"
                              className="me-2 mb-1 px-2"
                              id="simsActionTerminate"
                              onClick={async () => {
                                handleHide();
                                setTimeout(() => {
                                  setOpenTerminateModal(true);
                                }, 350);
                                await new Promise((resolve) => {
                                  const interval = setInterval(() => {
                                    if (!openResumeModalRef.current) {
                                      clearInterval(interval);
                                      resolve();
                                    }
                                  }, 500);
                                });
                              }}
                              style={{
                                "--dynamic-bg-color":
                                  whitelabel?.backgroundColor ?? "#0E1A2B",
                                "--dynamic-font-color":
                                  whitelabel?.fontColor ?? "#fff",
                                border: "none",
                              }}
                              disabled={
                                (activeUser.role === "subaccount" &&
                                  !activeUser.privileges.some(
                                    (privilege) =>
                                      privilege.name === "TerminateSIM"
                                  )) ||
                                activeUser.role === "lead"
                              }
                            >
                              <span> {t("Terminate")}</span>
                            </Button>
                          </span>
                        </OverlayTrigger>
                      )}
                      {/* {props.shownSim.status === "OnStock" && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            activeUser.role === "subaccount" &&
                            !activeUser.privileges.some(
                              (privilege) => privilege.name === "TestModeSIM"
                            ) ? (
                              renderTooltip
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className="d-inline-block">
                            <Button
                              variant="info"
                              className="me-2 mb-1 px-2"
                              id="simsActionTest"
                              onClick={() => setOpenTestModeModal(true)}
                              style={{
                                "--dynamic-bg-color":
                                  whitelabel?.backgroundColor ?? "#0E1A2B",
                                "--dynamic-font-color":
                                  whitelabel?.fontColor ?? "#fff",
                                border: "none",
                              }}
                              disabled={
                                activeUser.role === "subaccount" &&
                                !activeUser.privileges.some(
                                  (privilege) =>
                                    privilege.name === "TestModeSIM"
                                )
                              }
                            >
                              {t("Test Mode")}
                            </Button>
                          </span>
                        </OverlayTrigger>
                      )} */}
                    </div>
                  </Row>
                )}
                <Table size="sm" className="my-2">
                  <tbody>
                    <tr>
                      <th>{t("Usage")}</th>
                      <td>
                        {props.shownSim.totalBytes !== null
                          ? `${parseFloat(props.shownSim.totalMBytes).toFixed(
                              3
                            )}MB`
                          : "0.00MB"}
                      </td>
                    </tr>
                    <tr>
                      <th>{t("IP Address")}</th>
                      <td>
                        {props.shownSim.ipAddress !== null
                          ? props.shownSim.ipAddress
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>IMEI</th>
                      <td>
                        {props.shownSim.imei !== null
                          ? props.shownSim.imei
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>{t("Status")}</th>
                      <td>
                        <Badge
                          bg={
                            props.shownSim.status === "Productive"
                              ? "primary"
                              : props.shownSim.status === "Active"
                              ? "success"
                              : props.shownSim.status === "Suspended"
                              ? "warning"
                              : props.shownSim.status === "Terminated"
                              ? "danger"
                              : props.shownSim.status === "OnStock"
                              ? "light"
                              : props.shownSim.status === "Deleted"
                              ? "danger"
                              : props.shownSim.status === "TestProductive"
                              ? "primary"
                              : props.shownSim.status === "AutoSuspended"
                              ? "warning"
                              : null
                          }
                        >
                          {t(props.shownSim.status)}
                        </Badge>
                      </td>
                    </tr>
                    {props.shownSim.customFieldValues &&
                      props.shownSim.customFieldValues.length > 0 && (
                        <React.Fragment>
                          {props.shownSim.customFieldValues.map(
                            (customFieldValue) => {
                              return (
                                <tr>
                                  <th>{customFieldValue.displayName}</th>
                                  <td>{customFieldValue.value}</td>
                                </tr>
                              );
                            }
                          )}
                        </React.Fragment>
                      )}
                    <tr>
                      <th>{t("Visit Profile")}</th>
                      <td>
                        <span
                          onClick={() => {
                            if (!props.demoMode) {
                              navigate(`/sims/profile/${props.shownSim.imsi}`);
                            }
                          }}
                        >
                          <ExternalLink size={18} />
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <hr />
                <strong>{t("Map")}</strong>
                <SimsMap
                  lat={parseFloat(cellInfo?.latitude)}
                  lon={parseFloat(cellInfo?.longitude)}
                  className="my-3"
                />
                <Table size="sm">
                  <tbody>
                    <tr>
                      <th>{t("Provider")}</th>
                      <td>{props.shownSim.sku}</td>
                    </tr>
                    <tr>
                      <th>ICCID</th>
                      <td>{props.shownSim.iccid}</td>
                    </tr>
                    <tr>
                      <th>MSISDN</th>
                      <td>
                        {props.shownSim.msisdn !== ""
                          ? props.shownSim.msisdn
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>IMSI</th>
                      <td>
                        {props.shownSim.imsi !== ""
                          ? props.shownSim.imsi
                          : "N/A"}
                      </td>
                    </tr>
                    {props.shownSim.offer && (
                      <tr>
                        <th>Offer</th>
                        <td>
                          {props.shownSim.offer.name !== ""
                            ? props.shownSim.offer.name
                            : "N/A"}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <th>{t("Area")}</th>
                      <td>{cellInfo?.area ? cellInfo?.area : "N/A"}</td>
                    </tr>
                    <tr>
                      <th>{t("Cell")}</th>
                      <td>{cellInfo?.cell ? cellInfo?.cell : "N/A"}</td>
                    </tr>
                    <tr>
                      <th>{t("Longitude")}</th>
                      <td>
                        {cellInfo?.longitude ? cellInfo?.longitude : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>{t("Latitude")}</th>
                      <td>{cellInfo?.latitude ? cellInfo?.latitude : "N/A"}</td>
                    </tr>
                    <tr>
                      <th>{t("Cell Range")}</th>
                      <td>{cellInfo.range ? cellInfo.range : "N/A"}</td>
                    </tr>
                    <tr>
                      <th>{t("Unit")}</th>
                      <td>{cellInfo?.unit ? cellInfo?.unit : "N/A"}</td>
                    </tr>
                  </tbody>
                </Table>
                <hr />

                <strong>{t("Activity")}</strong>

                <ul className="timeline mt-2">
                  {props.shownSim.simActivity.map((activityElement, index) => (
                    <React.Fragment key={index}>
                      {activityElement.type === "ACTIVATE" && (
                        <li className="timeline-item">
                          <strong>Activated</strong>
                          <span className="float-end text-muted text-sm">
                            {activityElement.createdAt}
                          </span>
                          <p>{activityElement.description}</p>
                        </li>
                      )}
                      {activityElement.type === "SUSPEND" && (
                        <li className="timeline-item">
                          <strong>Suspended</strong>
                          <span className="float-end text-muted text-sm">
                            {activityElement.createdAt}
                          </span>
                          <p>{activityElement.description}</p>
                        </li>
                      )}
                      {activityElement.type === "RESUME" && (
                        <li className="timeline-item">
                          <strong>Resumed</strong>
                          <span className="float-end text-muted text-sm">
                            {activityElement.createdAt}
                          </span>
                          <p>{activityElement.description}</p>
                        </li>
                      )}
                      {activityElement.type === "MODIFY" && (
                        <li className="timeline-item">
                          <strong>Modified</strong>
                          <span className="float-end text-muted text-sm">
                            {activityElement.createdAt}
                          </span>
                          <p>{activityElement.description}</p>
                        </li>
                      )}
                      {activityElement.type === "TERMINATE" && (
                        <li className="timeline-item">
                          <strong>Terminated</strong>
                          <span className="float-end text-muted text-sm">
                            {activityElement.createdAt}
                          </span>
                          <p>{activityElement.description}</p>
                        </li>
                      )}
                    </React.Fragment>
                  ))}
                </ul>
              </React.Fragment>
            )}
            {(!props.shownSim || !props.shownSim.id) && (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner variant="primary" animation="border" />
              </div>
            )}
          </Offcanvas.Body>
        </Offcanvas>
        <ActivateSimModal
          openActivateModal={openActivateModal}
          selectedSims={[props.shownSim]}
          setOpenActivateModal={setOpenActivateModal}
          clearSelectedRows={props.clearSelectedRows}
        />
        <SuspendSimModal
          openSuspendModal={openSuspendModal}
          sims={[props.shownSim]}
          setOpenSuspendModal={setOpenSuspendModal}
          clearSelectedRows={props.clearSelectedRows}
        />
        <ResumeSimModal
          openResumeModal={openResumeModal}
          sims={[props.shownSim]}
          setOpenResumeModal={setOpenResumeModal}
          clearSelectedRows={props.clearSelectedRows}
        />
        <TerminateSimModal
          openTerminateModal={openTerminateModal}
          sims={[props.shownSim]}
          setOpenTerminateModal={setOpenTerminateModal}
          clearSelectedRows={props.clearSelectedRows}
        />
      </React.Fragment>
    );
  }
}

export default Single;
